/**
 * BuilderOnboarding — Inline 2-step personalization that lives in the chat panel.
 * Renders IN the left column where IrisChat normally goes (400px wide).
 * The site preview is visible the entire time — no modal blocking the view.
 *
 * Step 0: Personalize (city, voice type, mood, Instagram)
 * Step 1: Your Photos (portrait, portfolio, gallery embed)
 * Then: success message + transition to Iris
 */
import { useState, useCallback, useEffect } from 'react';
import { Sparkles, Loader2, Upload, Check, Globe, ArrowRight, ArrowLeft, Camera, Image as ImageIcon, Link } from 'lucide-react';
import axios from 'axios';
import LoadingView from '../demo/LoadingView';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const MOOD_CHIPS = [
  'Light & Airy', 'Dark & Moody', 'Film & Analog', 'Editorial',
  'Bohemian', 'Classic & Timeless', 'Whimsical', 'Documentary', 'Cinematic',
];

const BuilderOnboarding = ({ siteId, siteName, businessType, onComplete }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Pre-populated from site (NOT re-asked)
  const [studioName] = useState(siteName || '');
  const [workTypes] = useState(() => {
    if (!businessType) return ['Wedding Photography'];
    const types = businessType.split(',').map(t => t.trim());
    const map = { photographer: 'Wedding Photography', videographer: 'Wedding Videography', content_creator: 'Content Creation' };
    const mapped = types.map(t => map[t] || t).filter(Boolean);
    return mapped.length > 0 ? mapped : ['Wedding Photography'];
  });

  // Step 0: Personalize
  const [city, setCity] = useState('');
  const [voiceType, setVoiceType] = useState('solo');
  const [moodChips, setMoodChips] = useState([]);
  const [instagram, setInstagram] = useState('');

  // Defaults for deferred fields
  const [styleDesc] = useState('');
  const [dreamClient] = useState('');
  const [pricingMode] = useState('starting');
  const [startingPrice] = useState('');
  const [packages] = useState([]);
  const [bookingStatus] = useState('booking');
  const [calendlyUrl] = useState('');
  const [inquiryEmail] = useState('');
  const [tagline] = useState('');
  const socials = { instagram, tiktok: '', youtube: '', facebook: '', pinterest: '', vimeo: '', twitter: '' };

  // Step 1: Photos
  const [portraitFile, setPortraitFile] = useState(null);
  const [portfolioFiles, setPortfolioFiles] = useState([]);
  const [galleryUrl, setGalleryUrl] = useState('');

  // Loading progress
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const bookingYear = new Date().getFullYear() + 1;

  const loadingMessages = [
    "Reading your site...",
    "Getting to know your brand...",
    "Writing your story...",
    `Targeting couples in ${city.split(',')[0] || 'your area'}...`,
    "Crafting personalized copy...",
    "Polishing every detail...",
    "Almost done...",
  ];

  useEffect(() => {
    if (!loading) { setLoadingMsgIdx(0); setLoadingProgress(0); return; }
    const msgTimer = setInterval(() => {
      setLoadingMsgIdx(i => Math.min(i + 1, loadingMessages.length - 1));
    }, 12000);
    const progressTimer = setInterval(() => {
      setLoadingProgress(p => {
        if (p < 90) return p + 0.75;
        if (p < 95) return p + 0.1;
        return p;
      });
    }, 1000);
    return () => { clearInterval(msgTimer); clearInterval(progressTimer); };
  }, [loading, loadingMessages.length]);

  const handlePersonalize = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    const workTypeLabel = workTypes.join(' & ');
    const styleAll = [styleDesc, ...moodChips].filter(Boolean).join(', ');

    let personality = `${voiceType === 'team' ? 'We are' : 'I am'} ${studioName}, based in ${city}. `;
    personality += `${voiceType === 'team' ? 'We specialize' : 'I specialize'} in ${workTypeLabel.toLowerCase()}. `;
    if (styleAll) personality += `${voiceType === 'team' ? 'Our' : 'My'} style: ${styleAll}. `;
    if (dreamClient) personality += `${voiceType === 'team' ? 'Our' : 'My'} dream clients are: ${dreamClient}. `;
    if (tagline) personality += `${voiceType === 'team' ? 'Our' : 'My'} tagline is: "${tagline}". `;
    personality += `IMPORTANT: Use "${voiceType === 'team' ? 'we/us/our' : 'I/me/my'}" pronouns throughout ALL copy.`;

    if (pricingMode === 'starting' && startingPrice) {
      personality += ` Investment/pricing section should say: Starting at ${startingPrice}.`;
    } else if (pricingMode === 'full') {
      const pkgList = packages.filter(p => p.name && p.price).map(p => `${p.name} (${p.price}): ${p.includes}`).join('; ');
      if (pkgList) personality += ` Show these packages in the Investment section: ${pkgList}.`;
    } else if (pricingMode === 'none') {
      personality += ` Do NOT show any pricing or investment amounts. Instead use "Inquire for details" or similar.`;
    }

    if (bookingStatus === 'booking') personality += ` Show "Now Booking ${bookingYear}" prominently.`;
    else if (bookingStatus === 'limited') personality += ` Show "Limited Availability" badge.`;
    else if (bookingStatus === 'closed') personality += ` Show "Currently Closed for Bookings".`;

    const activeSocials = Object.entries(socials).filter(([_, v]) => v.trim());
    if (activeSocials.length > 0) {
      const socialList = activeSocials.map(([platform, url]) => {
        const handle = url.trim();
        const fullUrl = handle.startsWith('http') ? handle
          : platform === 'instagram' ? `https://instagram.com/${handle.replace('@', '')}`
          : platform === 'tiktok' ? `https://tiktok.com/@${handle.replace('@', '')}`
          : platform === 'youtube' ? `https://youtube.com/@${handle.replace('@', '')}`
          : platform === 'facebook' ? `https://facebook.com/${handle}`
          : platform === 'pinterest' ? `https://pinterest.com/${handle.replace('@', '')}`
          : platform === 'vimeo' ? `https://vimeo.com/${handle}`
          : platform === 'twitter' ? `https://x.com/${handle.replace('@', '')}`
          : handle;
        return `${platform}: ${fullUrl}`;
      }).join(', ');
      personality += ` SOCIAL LINKS — Add these to the footer with proper SVG icons (inline SVG, not emoji): ${socialList}. Use simple, elegant social icons in a row.`;
    }

    // ── PHASE 1: Fast, concrete operations (photos + gallery + profile) ──
    // These run FIRST and independently — even if copy-rewrite fails, photos land on the site.
    let warnings = [];

    // Upload portrait
    if (portraitFile) {
      try {
        const form = new FormData();
        form.append('files', portraitFile);
        const upRes = await axios.post(`${API}/sites/${siteId}/media/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
        if (upRes.data.urls?.length) {
          const mediaRes = await axios.get(`${API}/sites/${siteId}/media`, { withCredentials: true });
          const aboutImg = (mediaRes.data.media || []).find(m => m.section?.toLowerCase().includes('about'));
          if (aboutImg) {
            await axios.post(`${API}/sites/${siteId}/media/swap`, { old_url: aboutImg.url, new_url: upRes.data.urls[0] }, { withCredentials: true }).catch(() => {});
          }
        }
      } catch (e) {
        console.error('Portrait upload failed:', e);
        warnings.push('Portrait photo could not be uploaded');
      }
    }

    // Upload portfolio photos
    if (portfolioFiles.length > 0) {
      try {
        const form = new FormData();
        portfolioFiles.forEach(f => form.append('files', f));
        const upRes = await axios.post(`${API}/sites/${siteId}/media/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
        if (upRes.data.urls?.length) {
          const mediaRes = await axios.get(`${API}/sites/${siteId}/media`, { withCredentials: true });
          const galleryImgs = (mediaRes.data.media || []).filter(m => m.type === 'image');
          for (let i = 0; i < Math.min(upRes.data.urls.length, galleryImgs.length); i++) {
            await axios.post(`${API}/sites/${siteId}/media/swap`, { old_url: galleryImgs[i].url, new_url: upRes.data.urls[i] }, { withCredentials: true }).catch(() => {});
          }
        }
      } catch (e) {
        console.error('Portfolio upload failed:', e);
        warnings.push('Some portfolio photos could not be uploaded');
      }
    }

    // Embed gallery
    if (galleryUrl.trim()) {
      try {
        await axios.post(`${API}/sites/${siteId}/gallery`, { url: galleryUrl.trim(), title: 'Client Gallery' }, { withCredentials: true });
      } catch (e) {
        console.error('Gallery embed failed:', e);
        warnings.push('Gallery embed could not be added');
      }
    }

    // Save SEO profile + contact details (non-critical, fire and forget)
    axios.post(`${API}/seo/audit`, {
      site_id: siteId, studio_name: studioName, city,
      work_type: workTypeLabel, style_description: styleAll,
      starting_price: startingPrice, tagline,
    }, { withCredentials: true }).catch(() => {});

    const profileUpdates = {};
    if (inquiryEmail) profileUpdates.inquiry_email = inquiryEmail;
    if (bookingStatus) profileUpdates.availability = bookingStatus;
    if (calendlyUrl) profileUpdates.calendly_url = calendlyUrl;
    if (Object.keys(profileUpdates).length > 0) {
      axios.patch(`${API}/auth/profile`, profileUpdates, { withCredentials: true }).catch(() => {});
    }

    // ── PHASE 2: Copy rewrite (slow, fragile — runs after photos are safe) ──
    let copyRewriteSuccess = false;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await axios.post(`${API}/seo/copy-rewrite`, {
          site_id: siteId,
          vibe: moodChips[0] || 'warm',
          personality,
          dream_client: dreamClient || 'Couples who value authentic moments',
          differentiator: styleAll,
        }, { withCredentials: true, timeout: 300000 }); // 5 minute timeout
        copyRewriteSuccess = true;
        break;
      } catch (e) {
        console.error(`Copy rewrite attempt ${attempt + 1} failed:`, e);
        if (attempt === 0) {
          // Wait 5 seconds and retry once
          await new Promise(r => setTimeout(r, 5000));
        }
      }
    }

    if (!copyRewriteSuccess) {
      warnings.push('Copy personalization is still pending — ask Iris to personalize your copy in the builder');
    }

    // ── ALWAYS advance to success — even with warnings ──
    setLoading(false);
    if (warnings.length > 0) {
      setErrorMsg(warnings.join('. ') + '.');
    }
    setStep(2);
  }, [siteId, studioName, city, workTypes, voiceType, styleDesc, moodChips, dreamClient, tagline, pricingMode, startingPrice, packages, bookingStatus, calendlyUrl, inquiryEmail, socials, portraitFile, portfolioFiles, galleryUrl, bookingYear]);

  const handleSkipToBuilder = useCallback(async () => {
    await axios.patch(`${API}/sites/${siteId}`, { onboarding_complete: true }, { withCredentials: true }).catch(() => {});
    onComplete('builder');
  }, [siteId, onComplete]);

  const handlePublish = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/sites/${siteId}/publish`, {}, { withCredentials: true });
      await axios.patch(`${API}/sites/${siteId}`, { onboarding_complete: true }, { withCredentials: true }).catch(() => {});
      onComplete('published');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Publishing failed. You can try again from the builder.');
    }
    setLoading(false);
  }, [siteId, onComplete]);

  const canGoNext = () => step === 0 ? city.trim().length > 0 : true;

  const inputStyle = {
    width: '100%', padding: '12px 14px', fontSize: '14px', fontFamily: 'var(--sans)',
    background: '#fff', border: '1px solid #e8e3dc', color: '#2a2725',
    outline: 'none', borderRadius: '10px', transition: 'border-color .2s',
  };
  const focusStyle = (e) => e.currentTarget.style.borderColor = '#C9A84C';
  const blurStyle = (e) => e.currentTarget.style.borderColor = '#e8e3dc';

  // ─── LOADING STATE (inline in panel with full entertainment) ───
  if (loading) {
    return (
      <div data-testid="builder-onboarding" style={{
        width: '400px', display: 'flex', flexDirection: 'column', flexShrink: 0,
        background: '#f5f2ee', borderRight: '1px solid #e8e3dc',
        padding: '20px',
      }}>
        <LoadingView progress={loadingProgress} loadingMsg={loadingMessages[Math.min(loadingMsgIdx, loadingMessages.length - 1)]} />
      </div>
    );
  }

  // ─── SUCCESS STATE ───
  if (step === 2) {
    return (
      <div data-testid="builder-onboarding" style={{
        width: '400px', display: 'flex', flexDirection: 'column', flexShrink: 0,
        background: '#f5f2ee', borderRight: '1px solid #e8e3dc',
        alignItems: 'center', justifyContent: 'center', padding: '32px 24px',
      }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(91,168,157,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
          <Check size={28} style={{ color: '#5ba89d' }} />
        </div>
        <h2 style={{ fontFamily: 'var(--sans)', fontSize: '20px', fontWeight: 600, color: '#2a2725', marginBottom: '8px', textAlign: 'center' }}>Your site is personalized.</h2>
        <p style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: '#8c8782', marginBottom: '24px', lineHeight: 1.6, textAlign: 'center', maxWidth: '300px' }}>
          Publish now and start getting inquiries, or keep refining with Iris.
        </p>
        <button onClick={handlePublish} disabled={loading} data-testid="onboarding-publish-btn" style={{ width: '100%', maxWidth: '300px', padding: '14px', fontSize: '14px', fontFamily: 'var(--sans)', fontWeight: 600, background: '#C9A84C', color: '#fff', border: 'none', cursor: 'pointer', borderRadius: '10px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 2px 8px rgba(201,168,76,0.2)' }}>
          <Globe size={16} /> Publish My Site
        </button>
        <button onClick={handleSkipToBuilder} data-testid="onboarding-edit-btn" style={{ width: '100%', maxWidth: '300px', padding: '12px', fontSize: '13px', fontFamily: 'var(--sans)', fontWeight: 500, background: '#fff', color: '#8c8782', border: '1px solid #e8e3dc', cursor: 'pointer', borderRadius: '10px' }}>
          Keep editing with Iris
        </button>
        {errorMsg && <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8B7432', marginTop: '12px', textAlign: 'center', lineHeight: 1.5, padding: '10px 14px', background: 'rgba(201,168,76,0.06)', borderRadius: '8px', maxWidth: '300px' }}>{errorMsg}</p>}
      </div>
    );
  }

  // ─── FORM STEPS (inline in panel, chat-styled) ───
  return (
    <div data-testid="builder-onboarding" style={{
      width: '400px', display: 'flex', flexDirection: 'column', flexShrink: 0,
      background: '#f5f2ee', borderRight: '1px solid #e8e3dc',
    }}>
      {/* Header — matches IrisChat header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8e3dc', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(201,168,76,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Sparkles size={16} style={{ color: '#C9A84C' }} />
        </div>
        <div>
          <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', fontWeight: 600, color: '#2a2725' }}>Iris</p>
          <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>Setting up your site</p>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
        {/* Iris greeting message */}
        <div className="builder-msg-enter" style={{ display: 'flex', marginBottom: '16px' }}>
          <div style={{ maxWidth: '90%', padding: '14px 16px', background: '#fff', borderRadius: '16px 16px 16px 4px', fontFamily: 'var(--sans)', fontSize: '14px', lineHeight: 1.6, color: '#2a2725', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            {step === 0
              ? "I built this site just for you. A few quick details and I'll personalize all the copy to sound like your brand."
              : "Great! Now add your real photos to replace the placeholders. You can always swap these later."
            }
          </div>
        </div>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', justifyContent: 'center' }}>
          {[0, 1].map(i => (
            <div key={i} style={{ width: i <= step ? '24px' : '8px', height: '4px', borderRadius: '2px', background: i <= step ? '#C9A84C' : '#e8e3dc', transition: 'all .3s' }} />
          ))}
        </div>

        {errorMsg && (
          <div style={{ margin: '0 0 12px', padding: '10px 14px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8B7432', flex: 1 }}>{errorMsg}</p>
            <button onClick={() => setErrorMsg(null)} style={{ background: 'none', border: 'none', color: '#8B7432', cursor: 'pointer', fontSize: '14px' }}>x</button>
          </div>
        )}

        {/* ═══ STEP 0: Personalize ═══ */}
        {step === 0 && (
          <div className="builder-step-enter" data-testid="onboarding-step-personalize" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <input value={city} onChange={e => setCity(e.target.value)} placeholder="Where are you based? (e.g. Austin, TX)" style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} data-testid="onboarding-city" autoFocus />
              <p style={{ fontFamily: 'var(--sans)', fontSize: '10px', color: '#bfb9b3', marginTop: '4px' }}>Helps target local couples</p>
            </div>

            <div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#2a2725', marginBottom: '6px', fontWeight: 500 }}>Voice</p>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[{ key: 'solo', label: "Just me", sub: '"I" / "my"' }, { key: 'team', label: "We're a team", sub: '"we" / "our"' }].map(v => (
                  <button key={v.key} onClick={() => setVoiceType(v.key)} data-testid={`onboarding-voice-${v.key}`} style={{ flex: 1, padding: '10px', textAlign: 'center', borderRadius: '10px', background: voiceType === v.key ? '#C9A84C' : '#fff', color: voiceType === v.key ? '#fff' : '#2a2725', border: `1.5px solid ${voiceType === v.key ? '#C9A84C' : '#e8e3dc'}`, cursor: 'pointer', transition: 'all .15s' }}>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, fontFamily: 'var(--sans)' }}>{v.label}</span>
                    <span style={{ display: 'block', fontSize: '10px', opacity: voiceType === v.key ? 0.85 : 0.5, fontFamily: 'var(--sans)' }}>{v.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#2a2725', marginBottom: '6px', fontWeight: 500 }}>Your vibe <span style={{ fontWeight: 400, color: '#bfb9b3' }}>(optional)</span></p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {MOOD_CHIPS.map(m => {
                  const sel = moodChips.includes(m);
                  return (
                    <button key={m} onClick={() => setMoodChips(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])} data-testid={`onboarding-mood-${m.toLowerCase().replace(/ & /g, '-')}`} style={{ padding: '6px 12px', fontSize: '11px', fontFamily: 'var(--sans)', fontWeight: 500, background: sel ? 'rgba(201,168,76,0.1)' : '#fff', color: sel ? '#8B7432' : '#8c8782', border: `1px solid ${sel ? '#C9A84C' : '#e8e3dc'}`, cursor: 'pointer', borderRadius: '9999px', transition: 'all .15s' }}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: instagram ? 'rgba(201,168,76,0.08)' : '#f0ece6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill={instagram ? '#C9A84C' : '#bfb9b3'}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
              </div>
              <input value={instagram} onChange={e => setInstagram(e.target.value)} placeholder="Instagram (optional)" style={{ ...inputStyle, padding: '10px 12px', fontSize: '13px' }} onFocus={focusStyle} onBlur={blurStyle} data-testid="onboarding-instagram" />
            </div>
          </div>
        )}

        {/* ═══ STEP 1: Photos ═══ */}
        {step === 1 && (
          <div className="builder-step-enter" data-testid="onboarding-step-photos" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#2a2725', marginBottom: '6px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px' }}><Camera size={13} style={{ color: '#C9A84C' }} /> Your portrait</p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px', border: '1px dashed #e8e3dc', borderRadius: '10px', cursor: 'pointer', transition: 'border-color .2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#C9A84C'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e3dc'}>
                <Upload size={14} style={{ color: '#C9A84C' }} />
                <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: portraitFile ? '#2a2725' : '#8c8782' }}>
                  {portraitFile ? portraitFile.name : 'Choose a photo for your About section'}
                </span>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) setPortraitFile(e.target.files[0]); }} />
              </label>
            </div>

            <div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#2a2725', marginBottom: '6px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px' }}><ImageIcon size={13} style={{ color: '#C9A84C' }} /> Your best work (up to 20)</p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px', border: '1px dashed #e8e3dc', borderRadius: '10px', cursor: 'pointer', transition: 'border-color .2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#C9A84C'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e3dc'}>
                <Upload size={14} style={{ color: '#C9A84C' }} />
                <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: portfolioFiles.length > 0 ? '#2a2725' : '#8c8782' }}>
                  {portfolioFiles.length > 0 ? `${portfolioFiles.length} photo${portfolioFiles.length > 1 ? 's' : ''} selected` : 'Choose photos (select multiple)'}
                </span>
                <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { if (e.target.files?.length) setPortfolioFiles(Array.from(e.target.files).slice(0, 20)); }} />
              </label>
            </div>

            <div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#2a2725', marginBottom: '6px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px' }}><Link size={13} style={{ color: '#C9A84C' }} /> Gallery embed (optional)</p>
              <input value={galleryUrl} onChange={e => setGalleryUrl(e.target.value)} placeholder="Pic-Time, Pixieset, or SmugMug URL" style={{ ...inputStyle, fontSize: '13px' }} onFocus={focusStyle} onBlur={blurStyle} />
            </div>
          </div>
        )}
      </div>

      {/* Bottom actions — fixed at bottom of panel */}
      <div style={{ padding: '12px 20px 16px', borderTop: '1px solid #e8e3dc' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} style={{ padding: '12px 16px', fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 500, background: '#fff', color: '#8c8782', border: '1px solid #e8e3dc', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ArrowLeft size={13} /> Back
            </button>
          )}
          {step === 0 ? (
            <button onClick={() => setStep(1)} disabled={!canGoNext()} data-testid="onboarding-next-btn" style={{
              flex: 1, padding: '12px', fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600,
              background: canGoNext() ? '#C9A84C' : '#e8e3dc', color: canGoNext() ? '#fff' : '#8c8782',
              border: 'none', borderRadius: '10px', cursor: canGoNext() ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all .15s',
              boxShadow: canGoNext() ? '0 2px 8px rgba(201,168,76,0.2)' : 'none',
            }}>
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={handlePersonalize} data-testid="onboarding-personalize-btn" style={{
              flex: 1, padding: '12px', fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600,
              background: '#C9A84C', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              boxShadow: '0 2px 8px rgba(201,168,76,0.2)',
            }}>
              <Sparkles size={14} /> Personalize My Site
            </button>
          )}
        </div>
        <button onClick={handleSkipToBuilder} data-testid="onboarding-skip-btn" style={{ display: 'block', width: '100%', margin: '8px 0 0', fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center', padding: '6px' }}>
          Skip — I'll do this later
        </button>
      </div>
    </div>
  );
};

export default BuilderOnboarding;
